import styled from "@emotion/styled";
import {COLORS} from "../../../constants/constants";
import {Maybe, OrderEvent} from "../../../generated/graphql";
import {Text} from "@blueprintjs/core";
import {NoteIcon} from "../icons/NoteIcon";
import {getDateTimeAtSpecificTimeZone, getIANATimezone, getLocalDateTime} from "../../../utils/DateTimeUtils";
import {toDate} from "date-fns-tz";

interface INoteItemProps {
  note: OrderEvent;
  timeZoneOfJobStop?: boolean;
  isTimeFormat24hr?: boolean;
  timezone?: Maybe<string>;
}

const NoteItem = ({note, isTimeFormat24hr = false, timeZoneOfJobStop = false, timezone}: INoteItemProps) => {
  const {eventDateTime, note: noteText, userName} = note;

  const timeFormat = isTimeFormat24hr ? "d MMM yyyy, HH:mm zzz" : "d MMM yyyy, hh:mm aaa zzz";
  const ianaTimezone = timezone ? getIANATimezone(timezone) : "";
  const eventDateTimeWithZone = ianaTimezone && eventDateTime ? `${eventDateTime} ${ianaTimezone}` : eventDateTime;

  const isoString = eventDateTimeWithZone ? toDate(eventDateTimeWithZone).toISOString() : "";

  const formattedTime = timeZoneOfJobStop
    ? getDateTimeAtSpecificTimeZone({
        dateStr: isoString,
        timeZone: timezone,
        timeFormat: timeFormat
      })
    : getLocalDateTime({
        dateStr: isoString,
        timeZone: timezone,
        timeFormat: timeFormat
      });

  return (
    <Container data-testid="dispatch-note-item">
      <NoteContent>
        <IconContainer>
          <NoteIcon />
        </IconContainer>
        <Text style={{lineHeight: "20px", wordBreak: "break-word", whiteSpace: "pre-wrap"}}>{noteText}</Text>
      </NoteContent>
      <NoteMeta>
        <GrayText style={{flexShrink: 1}} ellipsize>
          {userName}
        </GrayText>
        <GrayText style={{flexShrink: 0}}>{formattedTime}</GrayText>
      </NoteMeta>
    </Container>
  );
};

export default NoteItem;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const NoteContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background-color: ${COLORS.BG_GRAY_LIGHT};
  padding: 8px;
  border-radius: 8px;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  height: 20px;
`;

const NoteMeta = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
`;

const GrayText = styled(Text)`
  color: #6b7280;
`;
